import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatCharacterSignedNumber } from '../../utils/CharacterPrintPresentation';
import './CharacterPrintDocument.css';

const formatItemState = (item) => [
  item.equipped ? 'Equipped' : '',
  item.attuned ? 'Attuned' : '',
  item.requiresAttunement && !item.attuned ? 'Attunement required' : ''
].filter(Boolean).join(', ');

const PrintSection = ({ title, className = '', children }) => (
  <section className={`character-print-section ${className}`.trim()}>
    <h2>{title}</h2>
    {children}
  </section>
);

const getHandwritingRowCount = (rowCount, roomyCount, denseCount = 2) => (
  rowCount >= 30 ? denseCount : rowCount >= 12 ? Math.max(denseCount, roomyCount - 2) : roomyCount
);

const PrintWeaponTable = ({ rows, title = 'Equipped Weapons', handwritingRows = 0 }) => {
  const visibleRows = Array.isArray(rows) ? rows : [];
  if (visibleRows.length === 0 && handwritingRows === 0) return null;

  return (
    <PrintSection title={title} className="character-print-section--weapons">
      <table className="character-print-table character-print-weapon-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Attack</th>
            <th>Damage and Type</th>
            <th>Range</th>
            <th>Training</th>
            <th>Mastery</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((weapon) => (
            <tr key={weapon.key || weapon.name}>
              <td><strong>{weapon.name}</strong></td>
              <td>{typeof weapon.attackBonus === 'number' ? formatCharacterSignedNumber(weapon.attackBonus) : weapon.attackBonus}</td>
              <td>{weapon.damageText}</td>
              <td>{weapon.rangeText}</td>
              <td>{weapon.proficiencyText}</td>
              <td>{weapon.masteryText}</td>
              <td>{weapon.notes}</td>
            </tr>
          ))}
          {Array.from({ length: handwritingRows }, (_, index) => (
            <tr key={`blank-weapon-${index}`} className="character-print-handwriting-row" aria-label="Blank weapon row">
              {Array.from({ length: 7 }, (__, cellIndex) => <td key={cellIndex}>&nbsp;</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </PrintSection>
  );
};

const PrintInventoryTable = ({ title, rows, magic = false, handwritingRows = 0 }) => {
  const visibleRows = Array.isArray(rows) ? rows : [];
  if (visibleRows.length === 0 && handwritingRows === 0) return null;

  return (
    <PrintSection title={visibleRows.length > 0 ? `${title} (${visibleRows.length})` : title} className="character-print-section--inventory">
      <table className="character-print-table character-print-inventory-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>{magic ? 'Rarity / Type' : 'Type'}</th>
            <th>State</th>
            <th>Weight</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((item) => (
            <tr key={item.key}>
              <td><strong>{item.name}</strong></td>
              <td>{item.quantity}</td>
              <td>{[magic ? item.rarity : '', item.type].filter(Boolean).join(' - ')}</td>
              <td>{formatItemState(item)}</td>
              <td>{item.weight}</td>
            </tr>
          ))}
          {Array.from({ length: handwritingRows }, (_, index) => (
            <tr key={`blank-${magic ? 'magic' : 'equipment'}-${index}`} className="character-print-handwriting-row" aria-label={`Blank ${title.toLowerCase()} row`}>
              {Array.from({ length: 5 }, (__, cellIndex) => <td key={cellIndex}>&nbsp;</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </PrintSection>
  );
};

const PrintEquippedLoadout = ({ loadout }) => {
  const slots = Array.isArray(loadout?.slots) ? loadout.slots : [];
  if (slots.length === 0) return null;

  return (
    <PrintSection title="Equipped Loadout" className="character-print-section--loadout">
      <div className="character-print-loadout-summary">
        <p>
          {loadout.hasItems
            ? `${loadout.equippedCount} equipped or held item${loadout.equippedCount === 1 ? '' : 's'}`
            : 'No equipped items recorded - use the blank locations to sketch your loadout.'}
        </p>
        <p>
          <strong>Attuned:</strong>{' '}
          {loadout.attunedItems.length > 0
            ? loadout.attunedItems.map((item) => item.name).join(', ')
            : '____________________________'}
        </p>
      </div>
      <div className="character-print-loadout-grid" aria-label="Equipped item locations">
        {slots.map((slot) => (
          <article key={slot.key} className={`character-print-loadout-slot character-print-loadout-slot--${slot.key}`}>
            <span className="character-print-loadout-number">{slot.marker}</span>
            <div>
              <h3>{slot.label}</h3>
              {slot.items.length > 0
                ? <p>{slot.items.map((item) => `${item.name}${item.attuned ? ' *' : ''}`).join(', ')}</p>
                : <span className="character-print-loadout-blank" aria-label={`Blank ${slot.label.toLowerCase()} line`} />}
            </div>
          </article>
        ))}
        <div className="character-print-loadout-figure" aria-hidden="true">
          <svg viewBox="0 0 140 250" focusable="false">
            <path className="character-print-loadout-aura" d="M70 5 C43 5 25 30 25 70 L25 178 C25 217 45 244 70 244 C95 244 115 217 115 178 L115 70 C115 30 97 5 70 5 Z" />
            <circle className="character-print-loadout-body" cx="70" cy="25" r="16" />
            <path className="character-print-loadout-body" d="M61 42 L59 49 C49 50 43 55 40 65 L43 116 C49 123 91 123 97 116 L100 65 C97 55 91 50 81 49 L79 42" />
            <path className="character-print-loadout-body" d="M42 63 C36 68 33 78 30 91 L20 122 C18 128 21 133 26 133 C30 133 33 130 35 125 L46 91 M98 63 C104 68 107 78 110 91 L120 122 C122 128 119 133 114 133 C110 133 107 130 105 125 L94 91" />
            <path className="character-print-loadout-body" d="M47 116 C49 132 48 142 45 154 L58 160 L70 146 L82 160 L95 154 C92 142 91 132 93 116" />
            <path className="character-print-loadout-body" d="M48 154 C50 174 51 200 50 225 L47 239 L60 239 L64 225 L70 163 L76 225 L80 239 L93 239 L90 225 C89 200 90 174 92 154" />
            <path className="character-print-loadout-detail" d="M48 76 C59 82 81 82 92 76 M46 112 L94 112 M50 132 C60 136 80 136 90 132 M49 219 L63 219 M77 219 L91 219" />
            {[
              [1, 38, 22, 'M48 22 L54 23'],
              [2, 70, 83, ''],
              [3, 115, 60, 'M105 60 L92 62'],
              [4, 16, 120, 'M26 120 L31 116'],
              [5, 116, 143, 'M106 143 L91 134'],
              [6, 108, 224, 'M98 224 L90 222']
            ].map(([marker, x, y, leader]) => (
              <g key={marker} data-loadout-location={marker}>
                {leader && <path className="character-print-loadout-leader" d={leader} />}
                <circle className="character-print-loadout-marker" cx={x} cy={y} r="10" />
                <text x={x} y={y + 3}>{marker}</text>
              </g>
            ))}
          </svg>
        </div>
      </div>
      <p className="character-print-loadout-key">* Attuned. Locations are a visual aid, not equipment-slot rules.</p>
    </PrintSection>
  );
};

const PrintWritingSpace = ({ lines = 28 }) => (
  <section className="character-print-chapter character-print-chapter--notes">
    <header className="character-print-chapter-heading">
      <p>At the Table</p>
      <h1>Session Notes</h1>
    </header>
    <p className="character-print-writing-prompt">Conditions, expended supplies, new loot, clues, promises, and changes to copy back into VCS.</p>
    <div className="character-print-writing-lines" aria-label="Blank ruled writing space">
      {Array.from({ length: lines }, (_, index) => <span key={index} />)}
    </div>
  </section>
);

const PrintRuledLines = ({ lines, label }) => (
  <div className="character-print-writing-lines character-print-writing-lines--compact" aria-label={label}>
    {Array.from({ length: lines }, (_, index) => <span key={index} />)}
  </div>
);

const PrintBlankRows = ({ rows, columns, label }) => Array.from({ length: rows }, (_, index) => (
  <tr key={`${label}-${index}`} className="character-print-handwriting-row character-print-continuation-row" aria-label={label}>
    {Array.from({ length: columns }, (__, cellIndex) => <td key={cellIndex}>&nbsp;</td>)}
  </tr>
));

const PrintAdvancementWorksheet = ({ classProgression }) => {
  const classes = Array.isArray(classProgression) ? classProgression : [];
  const blankClassRows = Math.max(3, 6 - classes.length);
  return (
    <section className="character-print-chapter character-print-chapter--advancement">
      <header className="character-print-chapter-heading">
        <p>Player Worksheet</p>
        <h1>Advancement and Character Options</h1>
      </header>
      <p className="character-print-writing-prompt">Track multiclass levels, new choices, limited uses, and table rulings before updating the digital record.</p>
      <PrintSection title="Multiclass Progression">
        <table className="character-print-table character-print-advancement-table">
          <thead><tr><th>Class</th><th>Subclass</th><th>Level</th><th>Hit Die</th><th>Spellcasting</th><th>Notes</th></tr></thead>
          <tbody>
            {classes.map((entry) => (
              <tr key={entry.key}>
                <td><strong>{entry.name}</strong></td><td>{entry.subclass}</td><td>{entry.level}</td><td>{entry.hitDie}</td><td>{entry.spellcasting}</td><td>{entry.notes}</td>
              </tr>
            ))}
            <PrintBlankRows rows={blankClassRows} columns={6} label="Blank multiclass row" />
          </tbody>
        </table>
      </PrintSection>
      <PrintSection title="Additional Class Features">
        <table className="character-print-table character-print-feature-worksheet-table">
          <thead><tr><th>Level / Class</th><th>Feature</th><th>Uses</th><th>Reset</th><th>Rules and Notes</th></tr></thead>
          <tbody><PrintBlankRows rows={9} columns={5} label="Blank class feature continuation row" /></tbody>
        </table>
      </PrintSection>
      <PrintSection title="Additional Feats and Traits">
        <table className="character-print-table character-print-feature-worksheet-table">
          <thead><tr><th>Source</th><th>Feat / Trait</th><th>Uses</th><th>Reset</th><th>Rules and Notes</th></tr></thead>
          <tbody><PrintBlankRows rows={7} columns={5} label="Blank feat continuation row" /></tbody>
        </table>
      </PrintSection>
      <PrintRuledLines lines={6} label="Blank advancement notes" />
    </section>
  );
};

const PrintSpellContinuation = ({ spellcasting }) => (
  <section className="character-print-chapter character-print-chapter--spell-continuation">
    <header className="character-print-chapter-heading">
      <p>Player Worksheet</p>
      <h1>Spellbook Continuation</h1>
    </header>
    <section className="character-print-spellcasting-strip" aria-label="Writable spellcasting statistics">
      <div><span>Ability</span><strong>{spellcasting.ability || '_____'}</strong></div>
      <div><span>Save DC</span><strong>{spellcasting.saveDC ?? '_____'}</strong></div>
      <div><span>Attack</span><strong>{spellcasting.attackBonus != null ? formatCharacterSignedNumber(spellcasting.attackBonus) : '_____'}</strong></div>
      <div className="character-print-slots"><span>Slots / Pact Magic / Free Casts</span><strong>________________________________________________</strong></div>
    </section>
    <PrintSection title="Additional Spells">
      <table className="character-print-table character-print-spell-table">
        <thead><tr><th>Level</th><th>Prep</th><th>Spell</th><th>Time</th><th>Range</th><th>Components</th><th>Duration</th></tr></thead>
        <tbody><PrintBlankRows rows={18} columns={7} label="Blank spell continuation row" /></tbody>
      </table>
    </PrintSection>
    <PrintSection title="Spell Rules, Materials, and Casting Notes">
      <PrintRuledLines lines={10} label="Blank ruled spell notes" />
    </PrintSection>
  </section>
);

const PrintInventoryContinuation = () => (
  <section className="character-print-chapter character-print-chapter--inventory-continuation">
    <header className="character-print-chapter-heading">
      <p>Player Worksheet</p>
      <h1>Inventory Continuation</h1>
    </header>
    <PrintSection title="Additional Equipment">
      <table className="character-print-table character-print-inventory-table">
        <thead><tr><th>Item</th><th>Qty</th><th>Type / Container</th><th>State / Charges</th><th>Weight</th></tr></thead>
        <tbody><PrintBlankRows rows={14} columns={5} label="Blank equipment continuation row" /></tbody>
      </table>
    </PrintSection>
    <PrintSection title="Additional Magic Items">
      <table className="character-print-table character-print-inventory-table">
        <thead><tr><th>Magic Item</th><th>Qty</th><th>Rarity / Type</th><th>Attunement / Charges</th><th>Weight</th></tr></thead>
        <tbody><PrintBlankRows rows={8} columns={5} label="Blank magic item continuation row" /></tbody>
      </table>
    </PrintSection>
    <PrintSection title="Containers, Treasure, Encumbrance, and Supplies">
      <PrintRuledLines lines={8} label="Blank ruled inventory notes" />
    </PrintSection>
  </section>
);

const PrintWealth = ({ wealth }) => {
  const coins = wealth?.coins || {};
  const valuables = Array.isArray(wealth?.valuables) ? wealth.valuables : [];

  return (
    <PrintSection title="Wealth and Valuables" className="character-print-section--wealth">
      <div className="character-print-wealth">
        {['cp', 'sp', 'ep', 'gp', 'pp'].map((coin) => (
          <span key={coin}><strong>{coin.toUpperCase()}</strong> {Number(coins[coin]) > 0 ? coins[coin] : ''}</span>
        ))}
        {valuables.map((valuable) => (
          <span key={valuable.key}><strong>{valuable.name}</strong>{valuable.quantity > 1 ? ` x${valuable.quantity}` : ''}</span>
        ))}
        <span className="character-print-wealth-blank"><strong>Other</strong></span>
      </div>
    </PrintSection>
  );
};

const PrintItemDetails = ({ rows }) => {
  const describedItems = (rows || []).filter((item) => item.description);
  if (describedItems.length === 0) return null;

  return (
    <PrintSection title="Item Details" className="character-print-section--item-details">
      <div className="character-print-entry-list character-print-entry-list--columns">
        {describedItems.map((item) => (
          <article key={`detail-${item.key}`} className="character-print-entry">
            <h3>{item.name}</h3>
            <p className="character-print-entry-meta">
              {[item.rarity, item.type, item.quantity > 1 ? `Qty ${item.quantity}` : '', formatItemState(item)]
                .filter(Boolean)
                .join(' - ')}
            </p>
            <p>{item.description}</p>
          </article>
        ))}
      </div>
    </PrintSection>
  );
};

const CharacterPrintDocument = ({ model }) => {
  if (!model) return null;
  const allActionSections = Array.isArray(model.actionEconomy?.sections)
    ? model.actionEconomy.sections.filter((section) => Array.isArray(section.entries) && section.entries.length > 0)
    : [];
  const actionSections = allActionSections
    .map((section) => ({ ...section, entries: section.entries.filter((entry) => entry.type !== 'spell') }))
    .filter((section) => section.entries.length > 0);
  const spellActionSections = allActionSections
    .map((section) => ({ ...section, entries: section.entries.filter((entry) => entry.type === 'spell') }))
    .filter((section) => section.entries.length > 0);
  const hasActions = Boolean(model.actionEconomy?.movementSummary || allActionSections.length > 0 || model.additionalWeapons.length > 0);
  const hasSpells = model.spellcasting.spells.length > 0
    || model.spellcasting.slots.length > 0
    || model.spellcasting.ability
    || model.spellcasting.attackBonus != null
    || model.spellcasting.saveDC != null;
  const itemDetails = [...model.equipment, ...model.magicItems];

  return (
    <main className="character-print-document" aria-label={`${model.name} printable character sheet`}>
      <div className="character-print-running-header" aria-hidden="true">
        <span>{model.name}</span>
        <span>Virtual Combat Simulator - Complete Character Record</span>
      </div>

      <section className="character-print-core character-print-chapter">
        <header className="character-print-identity">
          <div className="character-print-identity-copy">
            <p className="character-print-kicker">Virtual Combat Simulator</p>
            <h1>{model.name}</h1>
            <p className="character-print-identity-line">
              {[model.classLine, model.species, model.background].filter(Boolean).join(' - ')}
            </p>
            {model.alignment && <p className="character-print-alignment">{model.alignment}</p>}
          </div>
          {model.portraitUrl && (
            <div className="character-print-portrait-frame">
              <img src={model.portraitUrl} alt="" className="character-print-portrait" />
            </div>
          )}
          <div className="character-print-level-block">
            <span>Level</span>
            <strong>{model.level}</strong>
            {model.experience > 0 && <small>{model.experience.toLocaleString()} XP</small>}
          </div>
        </header>

        <section className="character-print-combat-strip" aria-label="Combat statistics">
          {[
            ['Armor Class', model.combat.armorClass],
            ['Hit Points', `${model.combat.currentHp} / ${model.combat.maxHp}`],
            ['Temporary', model.combat.temporaryHp],
            ['Initiative', formatCharacterSignedNumber(model.combat.initiative)],
            ['Speed', model.combat.speed],
            ['Prof. Bonus', formatCharacterSignedNumber(model.combat.proficiencyBonus)],
            ['Passive Perception', model.combat.passivePerception],
            ['Size', model.combat.size]
          ].map(([label, value]) => (
            <div key={label} className="character-print-combat-stat">
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </section>

        <section className="character-print-resource-strip" aria-label="Character resources">
          <span><strong>Hit Dice:</strong> {model.combat.hitDice || '-'}</span>
          <span><strong>Inspiration:</strong> {model.combat.inspiration ? 'Yes' : 'No'}</span>
          <span><strong>Death Saves:</strong> {model.combat.deathSaves.successes} successes / {model.combat.deathSaves.failures} failures</span>
        </section>

        {(model.combat.defenses.length > 0 || model.combat.senses.length > 0) && (
          <section className="character-print-resource-strip" aria-label="Defenses and senses">
            {model.combat.defenses.length > 0 && <span><strong>Defenses:</strong> {model.combat.defenses.join('; ')}</span>}
            {model.combat.senses.length > 0 && <span><strong>Senses:</strong> {model.combat.senses.join('; ')}</span>}
          </section>
        )}
        {model.combat.armorClassSource && (
          <p className="character-print-calculation-note"><strong>AC:</strong> {model.combat.armorClassSource}</p>
        )}

        <section className="character-print-abilities" aria-label="Abilities, saving throws, and skills">
          {model.abilities.map((ability) => (
            <article key={ability.key} className="character-print-ability">
              <header>
                <span>{ability.shortLabel}</span>
                <strong>{ability.score}</strong>
                <b>{formatCharacterSignedNumber(ability.modifier)}</b>
              </header>
              <div className="character-print-skill character-print-skill--save">
                <span className={ability.savingThrow.proficient ? 'character-print-training-mark is-trained' : 'character-print-training-mark'}>
                  {ability.savingThrow.proficient ? 'P' : ''}
                </span>
                <strong>{formatCharacterSignedNumber(ability.savingThrow.value)}</strong>
                <span>{ability.label} Save</span>
              </div>
              {ability.skills.map((skill) => (
                <div key={skill.name} className="character-print-skill">
                  <span className={`character-print-training-mark${skill.expertise ? ' is-expert' : skill.proficient ? ' is-trained' : ''}`}>
                    {skill.expertise ? 'E' : skill.proficient ? 'P' : ''}
                  </span>
                  <strong>{formatCharacterSignedNumber(skill.value)}</strong>
                  <span>{skill.name}</span>
                </div>
              ))}
            </article>
          ))}
        </section>

        <PrintWeaponTable
          rows={model.coreWeapons}
          handwritingRows={getHandwritingRowCount(model.coreWeapons.length, 3, 1)}
        />

        <PrintSection title="Training, Proficiencies, and Languages" className="character-print-section--proficiencies">
          <div className="character-print-proficiency-grid">
            <div><strong>Armor</strong><span>{model.proficiencies.armor.labels.join(', ') || '-'}</span></div>
            <div><strong>Weapons</strong><span>{model.proficiencies.weapons.labels.join(', ') || '-'}</span></div>
            <div><strong>Tools</strong><span>{model.proficiencies.tools.join(', ') || '-'}</span></div>
            <div><strong>Languages</strong><span>{model.proficiencies.languages.join(', ') || '-'}</span></div>
          </div>
        </PrintSection>
        {(model.choices.selections.length > 0 || model.choices.unresolved.length > 0) && (
          <PrintSection title="Character Choices" className="character-print-section--choices">
            {model.choices.selections.length > 0 && <p><strong>Recorded:</strong> {model.choices.selections.join(' | ')}</p>}
            {model.choices.unresolved.length > 0 && <p><strong>Selection required:</strong> {model.choices.unresolved.join('; ')}</p>}
          </PrintSection>
        )}
      </section>

      {hasActions && (
        <section className="character-print-chapter">
          <header className="character-print-chapter-heading">
            <p>Combat Reference</p>
            <h1>Actions and Options</h1>
          </header>
          {model.actionEconomy?.movementSummary && (
            <p className="character-print-movement"><strong>Movement:</strong> {model.actionEconomy.movementSummary}</p>
          )}
          {actionSections.map((section) => (
            <PrintSection key={section.key} title={`${section.title} (${section.entries.length})`}>
              <div className="character-print-entry-list">
                {section.entries.map((entry) => (
                  <article key={entry.key} className="character-print-entry character-print-action-entry">
                    <div className="character-print-entry-heading">
                      <h3>{entry.name}</h3>
                      {Array.isArray(entry.meta) && entry.meta.length > 0 && (
                        <span>{entry.meta.join(' - ')}</span>
                      )}
                    </div>
                    {entry.description && <p>{entry.description}</p>}
                  </article>
                ))}
              </div>
            </PrintSection>
          ))}
          {spellActionSections.length > 0 && (
            <PrintSection title="Spellcasting by Action Economy" className="character-print-section--spell-action-index">
              <p className="character-print-writing-prompt">Names only here to prevent duplicate rules text; see the complete Spellbook chapter.</p>
              <div className="character-print-spell-action-index">
                {spellActionSections.map((section) => (
                  <p key={section.key}><strong>{section.title}:</strong> {section.entries.map((entry) => entry.name).join(', ')}</p>
                ))}
              </div>
            </PrintSection>
          )}
          <PrintWeaponTable rows={model.additionalWeapons} title="Additional Weapons" />
          <PrintSection title="Additional Combat Options">
            <table className="character-print-table character-print-action-notes-table">
              <thead><tr><th>Action / Option</th><th>Timing</th><th>Uses / Reset</th><th>Rules and Resolution</th></tr></thead>
              <tbody><PrintBlankRows rows={4} columns={4} label="Blank combat option row" /></tbody>
            </table>
          </PrintSection>
        </section>
      )}

      {model.featureGroups.length > 0 && (
        <section className="character-print-chapter">
          <header className="character-print-chapter-heading">
            <p>Character Reference</p>
            <h1>Features, Traits, and Feats</h1>
          </header>
          {model.featureGroups.map((group) => (
            <PrintSection key={group.title} title={group.title}>
              <div className="character-print-entry-list">
                {group.entries.map((entry) => (
                  <article key={entry.key} className="character-print-entry">
                    <h3>{entry.name}</h3>
                    {entry.description && <p>{entry.description}</p>}
                    {(entry.source || entry.uses) && (
                      <p className="character-print-entry-meta">
                        {[entry.source, entry.uses?.max ? `${entry.uses.max} uses` : ''].filter(Boolean).join(' - ')}
                      </p>
                    )}
                  </article>
                ))}
                {group.notes && (
                  <article className="character-print-entry">
                    <h3>Additional Notes</h3>
                    <p>{group.notes}</p>
                  </article>
                )}
              </div>
            </PrintSection>
          ))}
          <PrintSection title="Feature, Feat, and Trait Notes" className="character-print-section--feature-notes">
            <PrintRuledLines lines={22} label="Blank ruled feature notes" />
          </PrintSection>
        </section>
      )}

      {hasSpells && (
        <section className="character-print-chapter character-print-chapter--spellbook">
          <header className="character-print-chapter-heading">
            <p>Spellcasting Reference</p>
            <h1>Spells</h1>
          </header>
          <section className="character-print-spellcasting-strip" aria-label="Spellcasting statistics">
            <div><span>Ability</span><strong>{model.spellcasting.ability || '-'}</strong></div>
            <div><span>Save DC</span><strong>{model.spellcasting.saveDC ?? '-'}</strong></div>
            <div><span>Attack</span><strong>{model.spellcasting.attackBonus != null ? formatCharacterSignedNumber(model.spellcasting.attackBonus) : '-'}</strong></div>
            <div className="character-print-slots">
              <span>Spell Slots</span>
              <strong>{model.spellcasting.slots.map((slot) => `L${slot.level}: ${Math.max(0, slot.total - slot.used)} remaining of ${slot.total}`).join(' | ') || '-'}</strong>
            </div>
          </section>
          {model.spellcasting.spells.length > 0 && (
            <PrintSection title={`Spells (${model.spellcasting.spells.length})`}>
              <table className="character-print-table character-print-spell-table">
                <thead>
                  <tr>
                    <th>Level</th>
                    <th>Prep</th>
                    <th>Spell</th>
                    <th>Time</th>
                    <th>Range</th>
                    <th>Components</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {model.spellcasting.spells.map((spell) => (
                    <tr key={spell.key}>
                      <td>{spell.levelLabel}</td>
                      <td>{spell.prepared ? 'Yes' : ''}</td>
                      <td>
                        <strong>{spell.name}</strong>{spell.ritual ? ' (R)' : ''}{spell.concentration ? ' (C)' : ''}
                        {spell.acquisition && <small className="character-print-spell-source">{spell.acquisition}</small>}
                      </td>
                      <td>{spell.castingTime}</td>
                      <td>{spell.range}</td>
                      <td>{spell.components}</td>
                      <td>{spell.duration}</td>
                    </tr>
                  ))}
                  {Array.from({ length: getHandwritingRowCount(model.spellcasting.spells.length, 4) }, (_, index) => (
                    <tr key={`blank-spell-${index}`} className="character-print-handwriting-row" aria-label="Blank spell row">
                      {Array.from({ length: 7 }, (__, cellIndex) => <td key={cellIndex}>&nbsp;</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </PrintSection>
          )}
          {model.spellcasting.spells.some((spell) => spell.description || spell.material || spell.modification || spell.damage || spell.attackBonus != null) && (
            <PrintSection title="Spell Details">
              <div className="character-print-entry-list character-print-entry-list--columns">
                {model.spellcasting.spells.filter((spell) => spell.description || spell.material || spell.modification || spell.damage || spell.attackBonus != null).map((spell) => (
                  <article key={`spell-detail-${spell.key}`} className="character-print-entry">
                    <h3>{spell.name}</h3>
                    {spell.material && <p className="character-print-entry-meta">Material: {spell.material}</p>}
                    {spell.modification && <p className="character-print-entry-meta">{spell.modification}</p>}
                    {(spell.damage || spell.attackBonus != null) && (
                      <p className="character-print-entry-meta">
                        {[spell.damage, spell.attackBonus != null ? `${formatCharacterSignedNumber(spell.attackBonus)} spell attack` : ''].filter(Boolean).join(' - ')}
                      </p>
                    )}
                    {spell.description && <p>{spell.description}</p>}
                  </article>
                ))}
              </div>
            </PrintSection>
          )}
        </section>
      )}

      <PrintAdvancementWorksheet classProgression={model.classProgression} />

      {hasSpells && <PrintSpellContinuation spellcasting={model.spellcasting} />}

      <section className="character-print-chapter character-print-chapter--inventory">
        <header className="character-print-chapter-heading">
          <p>Carried Record</p>
          <h1>Equipment, Magic Items, and Wealth</h1>
        </header>
        <PrintEquippedLoadout loadout={model.equippedLoadout} />
        <PrintInventoryTable
          title="Equipment"
          rows={model.equipment}
          handwritingRows={getHandwritingRowCount(model.equipment.length, 6)}
        />
        <PrintInventoryTable
          title="Magic Items"
          rows={model.magicItems}
          magic
          handwritingRows={getHandwritingRowCount(model.magicItems.length, 4)}
        />
        <PrintWealth wealth={model.wealth} />
        <PrintItemDetails rows={itemDetails} />
      </section>

      <PrintInventoryContinuation />

      {(model.narrativeGroups.length > 0 || model.minions.length > 0) && (
        <section className="character-print-chapter character-print-chapter--narrative">
          <header className="character-print-chapter-heading">
            <p>Character Record</p>
            <h1>Background and Notes</h1>
          </header>
          {model.narrativeGroups.map((group) => (
            <PrintSection key={group.title} title={group.title}>
              <div className="character-print-narrative-grid">
                {group.fields.map((field) => (
                  <article key={field.label} className="character-print-narrative-field">
                    <h3>{field.label}</h3>
                    <p>{field.value}</p>
                  </article>
                ))}
              </div>
            </PrintSection>
          ))}
          {model.minions.length > 0 && (
            <PrintSection title="Minions and Followers">
              <div className="character-print-entry-list">
                {model.minions.map((entry) => (
                  <article key={entry.key} className="character-print-entry">
                    <h3>{entry.name}</h3>
                    {entry.description && <p>{entry.description}</p>}
                  </article>
                ))}
              </div>
            </PrintSection>
          )}
        </section>
      )}

      <PrintWritingSpace />
    </main>
  );
};

export const waitForCharacterPrintAssets = async (root) => {
  const fontsReady = typeof document !== 'undefined' && document.fonts?.ready
    ? document.fonts.ready.catch(() => undefined)
    : Promise.resolve();
  const images = root ? Array.from(root.querySelectorAll('img')) : [];
  const imageReady = images.map((image) => {
    if (image.complete) return Promise.resolve();
    if (typeof image.decode === 'function') return image.decode().catch(() => undefined);
    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  });
  await Promise.allSettled([fontsReady, ...imageReady]);
};

export const waitForCharacterPrintPaint = () => new Promise((resolve) => {
  const requestFrame = typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
    ? window.requestAnimationFrame.bind(window)
    : (callback) => setTimeout(callback, 0);
  requestFrame(() => requestFrame(resolve));
});

export const CharacterPrintPortal = ({ model }) => {
  const [mountNode, setMountNode] = useState(null);

  useEffect(() => {
    if (!model || typeof document === 'undefined') return undefined;
    const node = document.createElement('div');
    node.id = 'character-print-root';
    node.className = 'character-print-root';
    document.body.appendChild(node);
    setMountNode(node);
    return () => {
      if (node.parentNode) node.parentNode.removeChild(node);
    };
  }, [model]);

  if (!model || !mountNode) return null;
  return createPortal(<CharacterPrintDocument model={model} />, mountNode);
};

export default CharacterPrintDocument;
